import type { GetSeasonResponse } from "@/openapi/generated";

function toDate(value: unknown): Date | null {
  if (value == null || value === "") return null;
  const parsed = value instanceof Date ? value : new Date(String(value));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function isOnOrAfter(now: Date, start: Date | null): boolean {
  if (!start) return false;
  return startOfDay(now).getTime() >= startOfDay(start).getTime();
}

function isOnOrBefore(now: Date, end: Date | null): boolean {
  if (!end) return false;
  return startOfDay(now).getTime() <= startOfDay(end).getTime();
}

export function formatFeaturedSeasonDates(
  season: Pick<
    GetSeasonResponse,
    "seasonStartDate" | "seasonEndDate" | "isContinuous"
  >,
): string | null {
  if (season.isContinuous) return null;

  const start = toDate(season.seasonStartDate);
  const end = toDate(season.seasonEndDate);
  if (!start && !end) return null;

  const format = (date: Date) =>
    date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  if (start && end) return `${format(start)} – ${format(end)}`;
  if (start) return `From ${format(start)}`;
  if (end) return `Until ${format(end)}`;
  return null;
}

export function getFeaturedSeasonStatusLabel(
  season: Pick<
    GetSeasonResponse,
    | "onlineRegistrationStartDate"
    | "onlineRegistrationEndDate"
    | "seasonStartDate"
    | "seasonEndDate"
    | "isContinuous"
  >,
  now = new Date(),
): string | null {
  const registrationStart = toDate(season.onlineRegistrationStartDate);
  const registrationEnd = toDate(season.onlineRegistrationEndDate);
  if (
    registrationStart &&
    registrationEnd &&
    isOnOrAfter(now, registrationStart) &&
    isOnOrBefore(now, registrationEnd)
  ) {
    return "Registration is open";
  }

  const seasonStart = toDate(season.seasonStartDate);
  const seasonEnd = toDate(season.seasonEndDate);
  if (season.isContinuous && seasonStart && isOnOrAfter(now, seasonStart)) {
    return "Now in session";
  }
  if (
    seasonStart &&
    seasonEnd &&
    isOnOrAfter(now, seasonStart) &&
    isOnOrBefore(now, seasonEnd)
  ) {
    return "Now in session";
  }
  if (
    seasonStart &&
    startOfDay(seasonStart).getTime() > startOfDay(now).getTime()
  ) {
    return "Coming up";
  }

  return null;
}
